export function waterMaterialFactory(THREE){

/**
 * Water is a thin transparent interface over the actual excavated terrain. waterDepth
 * is the water column in world units, not distance to the middle of the lake.
 * Reflection capture is optional; the sky approximation is retained at low quality.
 */
function makeWaterMaterial(uniforms) {
  const defaults = {
    waterLevel: {value: .18},
    reflectionTexture: {value: null},
    reflectionMatrix: {value: new THREE.Matrix4()},
    reflectionEnabled: {value: 0},
    reflectionTexel: {value: new THREE.Vector2(1 / 640, 1 / 450)},
    waterSunColor: {value: new THREE.Color('#fff1d1')},
    waterDaylight: {value: 1},
    waterfallEnds: {value: new THREE.Vector2(.235, .665)},
  };
  for (const [key, value] of Object.entries(defaults)) uniforms[key] ??= value;
  const material = new THREE.ShaderMaterial({
    name: 'Clear depth water',
    uniforms,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    forceSinglePass: true,
    vertexShader: /* glsl */`
      varying vec3 vWorld;
      varying vec3 vNormal;
      varying float vDepth;
      varying float vRiver;
      attribute float waterDepth;
      attribute float riverProgress;
      uniform float time;
      uniform vec2 wind;
      void main() {
        vec3 p = position;
        float river = step(0.0, riverProgress);
        float wet = smoothstep(0.0, .10, waterDepth);
        float strength = min(length(wind), 2.0);
        // A shallow, broad wave displaces the silhouette. Fine ripples are normals.
        p.y += wet * (1.0 - river) * (.002 + strength * .0025)
          * (sin(p.x * 7.3 + p.z * 3.8 - time * 1.2)
             + .45 * sin(p.x * 12.1 - p.z * 8.0 + time * 1.1));
        vWorld = (modelMatrix * vec4(p, 1.0)).xyz;
        vNormal = normalize(mat3(modelMatrix) * normal);
        vDepth = max(waterDepth, 0.0);
        vRiver = riverProgress;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
      }
    `,
    fragmentShader: /* glsl */`
      precision highp float;
      varying vec3 vWorld;
      varying vec3 vNormal;
      varying float vDepth;
      varying float vRiver;
      uniform float time;
      uniform float rain;
      uniform float snow;
      uniform vec3 sun;
      uniform vec3 color;
      uniform float lightning;
      uniform vec2 wind;
      uniform vec2 rainOrigin;
      uniform float storm;
      uniform float waterLevel;
      uniform sampler2D reflectionTexture;
      uniform mat4 reflectionMatrix;
      uniform float reflectionEnabled;
      uniform vec2 reflectionTexel;
      uniform vec3 waterSunColor;
      uniform float waterDaylight;
      uniform vec2 waterfallEnds;
      const float PI = 3.14159265359;
      float sat(float x) { return clamp(x, 0.0, 1.0); }
      float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float noise(vec2 p) {
        vec2 i = floor(p), f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x),
                   mix(hash(i + vec2(0, 1)), hash(i + 1.0), f.x), f.y);
      }
      vec2 rotate2(vec2 p, float a) {
        float c = cos(a), s = sin(a);
        return vec2(c * p.x - s * p.y, s * p.x + c * p.y);
      }
      // Band-limited directional wave trains: no regular screen-space hatch marks.
      vec2 waveSlope(vec2 p, vec2 direction, float windSpeed, float fp) {
        float roughPatch = noise(p * 1.2 - direction * time * .28);
        float rough = .48 + windSpeed * (.35 + roughPatch * .8) + storm * .45;
        vec2 slope = vec2(0);
        vec2 d = direction;
        float phase = dot(p, d) * 13.0 - time * 1.7;
        slope += d * cos(phase + noise(p * 1.8) * 1.2) * .038;
        d = rotate2(direction, .65);
        slope += d * cos(dot(p, d) * 27.0 - time * 2.15 + 1.4) * .023;
        d = rotate2(direction, -.52);
        slope += d * cos(dot(p, d) * 47.0 - time * 2.8 + 4.0) * .016
          * (1.0 - smoothstep(.018, .065, fp));
        vec2 q = p * 17.0 - direction * time * .52;
        float h = noise(q);
        slope += vec2(noise(q + vec2(.14, 0)) - h,
                      noise(q + vec2(0, .14)) - h) * .22
          * (1.0 - smoothstep(.024, .08, fp));
        return slope * rough;
      }
      vec3 rainRings(vec2 p, float fp) {
        float amount = rain * mix(exp(-dot(p - rainOrigin, p - rainOrigin) / 1.8),
                                  1.0, sat(storm * 1.8));
        vec3 rings = vec3(0.0);
        if (amount < .005) return rings;
        vec2 q = p * 6.3, grid = floor(q);
        for (int x = -1; x <= 1; x++) for (int y = -1; y <= 1; y++) {
          vec2 cell = grid + vec2(float(x), float(y));
          float seed = hash(cell + 7.0);
          if (hash(cell + 23.0) > amount * .82) continue;
          vec2 center = cell + .15 + .70 * vec2(hash(cell), hash(cell + 3.0));
          float age = fract(time * (.8 + seed * .3) + seed * 8.0);
          vec2 r = q - center;
          float distance = length(r) + .0001;
          float phase = (distance - age * .75) * 32.0;
          float envelope = exp(-phase * phase * .1) * pow(1.0 - age, 2.0);
          envelope *= amount * (1.0 - smoothstep(.014, .06, fp));
          rings.xy += r / distance * cos(phase) * envelope * .07;
          rings.z += max(0.0, sin(phase)) * envelope;
        }
        return rings;
      }
      // Warped crossed bands make soft, connected light filaments, not polka dots.
      float caustic(vec2 p) {
        vec2 q = p * 5.4;
        q += vec2(noise(q * .43 + time * .12), noise(q * .49 - time * .1 + 13.0)) * 2.5;
        float a = sin(q.x + sin(q.y * 1.36 + time * .45));
        float b = sin(q.y * 1.22 + sin(q.x * .91 - time * .39));
        float filaments = pow(sat(1.0 - abs(a + b) * 2.1), 5.0);
        float second = pow(sat(1.0 - abs(a - b + .6) * 2.3), 7.0);
        return filaments + second * .45;
      }
      vec3 skyReflection(vec3 ray, vec3 light) {
        float up = sat(ray.y);
        vec3 sky = mix(vec3(.32, .43, .45), vec3(.12, .24, .32), sqrt(up));
        sky *= mix(.48, 1.0, waterDaylight) * (1.0 - storm * .45);
        float towardSun = pow(max(dot(ray, light), 0.0), 32.0);
        return sky + waterSunColor * towardSun * .20 + lightning * .35;
      }
      void main() {
        vec2 p = vWorld.xz;
        float river = step(0.0, vRiver);
        float falling = river * (1.0 - smoothstep(.40, .86, abs(vNormal.y)));
        float depth = max(vDepth, .003);
        float fp = max(length(dFdx(p)), length(dFdy(p)));
        float windSpeed = min(length(wind), 2.4);
        vec2 direction = normalize(wind + vec2(.21, .09));
        vec2 slope = waveSlope(p, direction, windSpeed, fp);
        vec3 rings = rainRings(p, fp);
        slope += rings.xy * (1.0 - falling);
        float flow = vRiver * 168.0 - time * 6.4;
        float flowTexture = noise(vec2(vRiver * 88.0 - time * 2.7, p.x * 24.0 + p.y * 6.0));
        slope += river * vec2(sin(flow + p.x * 14.0), cos(flow * .81 + p.y * 11.0)) * .024;
        vec3 baseNormal = vNormal.y < 0.0 ? -vNormal : vNormal;
        vec3 n = normalize(mix(vec3(0, 1, 0), baseNormal, falling * .82)
                           + vec3(-slope.x, 0, -slope.y));
        vec3 view = normalize(cameraPosition - vWorld);
        if (dot(n, view) < 0.0) n = -n;
        vec3 light = normalize(sun - vWorld);
        float viewCos = max(dot(n, view), .055);
        float grazing = 1.0 - viewCos;
        float fresnel = .020 + .98 * mix(pow(grazing, 5.0), pow(grazing, 3.0), .20);
        vec3 reflection = skyReflection(reflect(-view, n), light);
        if (reflectionEnabled > .5 && river < .5) {
          vec4 projection = reflectionMatrix * vec4(vWorld, 1.0);
          vec2 mirrorUV = projection.xy / max(.001, projection.w);
          vec2 offset = slope * (.025 + depth * .015);
          mirrorUV = clamp(mirrorUV + offset, reflectionTexel * 1.5, 1.0 - reflectionTexel * 1.5);
          vec4 mirror = texture2D(reflectionTexture, mirrorUV);
          reflection = reflection * (1.0 - mirror.a) + mirror.rgb;
        }
        // Beer-Lambert optical thickness controls transparency, so shallow stones
        // and submerged fish remain visible while the deepest basin reads blue.
        float opticalDepth = depth / max(.38, viewCos);
        float bodyAlpha = 1.0 - exp(-opticalDepth * .88);
        bodyAlpha = clamp(bodyAlpha, .035, .70);
        vec3 body = mix(vec3(.027, .235, .248), vec3(.007, .120, .168),
                        smoothstep(.06, .85, depth));
        body *= mix(.52, 1.0, waterDaylight) * (1.0 - .28 * storm);
        body = mix(body, color * vec3(.45, .88, 1.08), storm * .32);
        float sunVisibility = smoothstep(.02, .55, light.y);
        // Refracted bed-light has parallax with the actual column depth.
        vec2 bedPoint = p - view.xz * depth * .30 / max(viewCos, .30);
        float caustics = caustic(bedPoint) * exp(-depth * 1.5)
                       * (1.0 - storm * .82) * waterDaylight;
        float bedVisibility = smoothstep(.01, .10, depth) * (1.0 - falling);
        body += vec3(.075, .15, .125) * caustics * bedVisibility * .32;
        // A bright moving highlight is concentrated along the sun's reflection,
        // rather than painting the whole lake with a white translucent veil.
        vec3 halfVector = normalize(view + light);
        float sharpness = mix(850.0, 190.0, sat(windSpeed * .55 + storm * .4));
        float specular = pow(max(dot(n, halfVector), 0.0), sharpness)
                       * (sharpness + 2.0) * .0013 * sunVisibility;
        vec3 lightReturn = reflection * fresnel;
        lightReturn += waterSunColor * specular * (1.0 - storm * .82);
        lightReturn += vec3(.017, .031, .033) * rings.z;
        // Foam is local to drops and plunge pools. There is no white shoreline ring.
        float impact = exp(-pow((vRiver - waterfallEnds.x - .018) / .024, 2.0))
                     + exp(-pow((vRiver - waterfallEnds.y - .018) / .031, 2.0));
        float lace = noise(vec2(p.x * 44.0 + sin(flow) * .7, vWorld.y * 61.0 - time * 9.0));
        float streak = pow(sat(sin(p.x * 71.0 + p.y * 13.0 + flowTexture * 5.0)), 8.0);
        float foam = river * (falling * (.10 + streak * .24 + smoothstep(.56, .85, lace) * .20)
                     + impact * smoothstep(.45, .72, flowTexture) * .42);
        foam *= smoothstep(.002, .07, depth);
        vec3 foamColor = vec3(.57, .72, .71) * mix(.60, 1.0, waterDaylight);
        float alpha = bodyAlpha + fresnel * (1.0 - bodyAlpha);
        vec3 emit = body * bodyAlpha * (1.0 - fresnel) + lightReturn;
        emit = mix(emit, foamColor, foam);
        alpha = mix(alpha, .88, foam);
        // The stream fades into the same lake level, eliminating a painted mouth.
        float mouth = river > .5 ? smoothstep(waterLevel - .008, waterLevel + .10, vWorld.y) : 1.0;
        float edge = smoothstep(.001, .024, depth);
        float iceFront = sat(snow * 1.6 - depth * .43 + (noise(p * 5.0) - .5) * .10);
        float ice = smoothstep(.03, .32, iceFront);
        float frostCloud = noise(p * 2.3) * .65 + noise(p * 7.7 + 3.0) * .35;
        vec3 iceColor = mix(vec3(.32, .45, .48), vec3(.57, .65, .64), .28 + frostCloud * .64);
        iceColor += caustic(p * .72) * .021;
        emit = mix(emit, iceColor, ice);
        alpha = mix(alpha, .97, ice);
        emit += lightning * vec3(.10, .16, .18) * alpha;
        gl_FragColor = vec4(emit / max(alpha, .015), sat(alpha * edge * mouth));
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }
    `,
  });
  material.defaultAttributeValues.waterDepth = [.30];
  material.defaultAttributeValues.riverProgress = [-1];
  return material;
}

/** The visible vertical water column where the miniature is cut by the front glass. */
function makeWaterSectionMaterial(uniforms) {
  return new THREE.ShaderMaterial({
    name: 'Lake cutaway water column', uniforms, transparent: true, depthWrite: false,
    side: THREE.DoubleSide, forceSinglePass: true,
    vertexShader: /* glsl */`
      varying vec3 vWorld;
      void main() {
        vWorld = (modelMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */`
      precision highp float;
      varying vec3 vWorld;
      uniform float time;
      uniform float waterLevel;
      uniform float waterDaylight;
      uniform float storm;
      uniform float snow;
      uniform float lightning;
      void main() {
        float depth = max(waterLevel - vWorld.y, 0.0);
        float beam = pow(.5 + .5 * sin(vWorld.x * 15.0 + sin(vWorld.x * 7.0 + time * .8)
                     + depth * 2.4 - time * .7), 9.0);
        float second = pow(.5 + .5 * sin(vWorld.x * 28.0 - depth * 4.7 + time * .5), 14.0);
        vec3 body = mix(vec3(.012, .155, .175), vec3(.005, .045, .064), smoothstep(0.0, .83, depth));
        body += vec3(.06, .15, .14) * (beam + second * .35) * exp(-depth * 2.7) * .09;
        body *= mix(.52, 1.0, waterDaylight) * (1.0 - storm * .35);
        // A thin meniscus, then uninterrupted transparent depth rather than bands.
        body += vec3(.035, .075, .073) * exp(-depth * 110.0);
        float alpha = .36 + smoothstep(.02, .8, depth) * .28;
        float ice = snow * exp(-depth * 15.0);
        body = mix(body, vec3(.46, .61, .61), ice);
        body += lightning * .16;
        gl_FragColor = vec4(body, alpha);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }
    `,
  });
}

return {makeWaterMaterial,makeWaterSectionMaterial};
}
